import {Artifact, PasteAttachment} from "../../../types";

/**
 * A paste with more lines than this number becomes an attachment chip. The
 * input does not add it as text.
 */
export const PASTE_LINE_THRESHOLD = 3;

/**
 * The maximum number of characters in an attachment. The function cuts a
 * longer paste at this limit. The chip then shows that the function cut the
 * paste. The backend also limits the characters, at a higher number.
 */
export const PASTE_MAX_CHARS = 50000;

/** The maximum number of pastes in one message. The input refuses more. */
export const MAX_PASTE_ATTACHMENTS = 5;

/** The artifact type that the backend uses to store an attachment. */
export const ATTACHMENT_ARTIFACT_TYPE = "user_attachment";

/** The only attachment kind. It is text that the user pastes into the composer. */
export const PASTE_ATTACHMENT_KIND = "paste";

/** A paste that the input holds. The user has not sent it. */
export interface PendingPasteAttachment {
    id: string;
    content: string;
    lineCount: number;
    truncated: boolean;
}

/**
 * The function that a composer calls to submit. A `false` return value means
 * that a guard refused the submit. The input then keeps the text and the
 * attachments.
 */
export type SubmitWithAttachments = (
    message: string,
    attachments: PendingPasteAttachment[]
) => boolean | void;

/** The data that a chip needs to show. The chip can be pending or already sent. */
export interface PasteChipData {
    id: string;
    lineCount: number;
    truncated: boolean;
    /** The paste number in the thread. The backend assigns it during storage. */
    number?: number;
    /** The pasted text. A chip in the thread uses this text to show a preview. */
    content?: string;
}

export const countLines = (text: string) => text.split("\n").length;

/** Clipboard text can have any of the three line endings. */
export const normalizePastedText = (text: string) =>
    text.replace(/\r\n?/g, "\n");

export const isAttachablePaste = (text: string) =>
    countLines(text) > PASTE_LINE_THRESHOLD;

/** Cut the text at the limit. Do not split a surrogate pair. */
function sliceAtCharLimit(text: string) {
    const cut = text.slice(0, PASTE_MAX_CHARS);
    const lastUnit = cut.charCodeAt(cut.length - 1);
    const endsInHighSurrogate = lastUnit >= 0xd800 && lastUnit <= 0xdbff;
    return endsInHighSurrogate ? cut.slice(0, -1) : cut;
}

export function createPendingPasteAttachment(
    text: string
): PendingPasteAttachment {
    const truncated = text.length > PASTE_MAX_CHARS;
    const content = truncated ? sliceAtCharLimit(text) : text;
    return {
        id: crypto.randomUUID(),
        content,
        lineCount: countLines(content),
        truncated,
    };
}

export const toRequestAttachments = (
    attachments: PendingPasteAttachment[]
): PasteAttachment[] =>
    attachments.map(({content, truncated}) => ({
        type: PASTE_ATTACHMENT_KIND,
        content,
        ...(truncated && {truncated}),
    }));

/**
 * Local artifacts that substitute for the artifacts the backend creates. The
 * message that the user just sent shows its chips, with their numbers, before
 * the application reloads the thread. `firstNumber` comes from
 * `nextPasteNumber` for the current thread.
 */
export const toPasteArtifacts = (
    attachments: PendingPasteAttachment[],
    firstNumber: number
): Artifact[] =>
    attachments.map(({id, content, lineCount, truncated}, index) => ({
        id,
        type: ATTACHMENT_ARTIFACT_TYPE,
        schemaVersion: "1.0",
        source: "user" as const,
        payload: {
            attachmentType: PASTE_ATTACHMENT_KIND,
            number: firstNumber + index,
            lines: lineCount,
            chars: content.length,
            truncated,
            content,
        },
        createdAt: new Date().toISOString(),
    }));

/** The line count from the payload. If it is absent, count the lines in the content. */
export const readPasteLineCount = (
    payload: Record<string, unknown> | undefined
) => {
    const {lines, content} = payload ?? {};
    return typeof lines === "number"
        ? lines
        : countLines(typeof content === "string" ? content : "");
};

export const readPasteChip = (artifact: Artifact): PasteChipData => {
    const {truncated, number, content} = artifact.payload;
    return {
        id: artifact.id,
        lineCount: readPasteLineCount(artifact.payload),
        truncated: truncated === true,
        ...(typeof number === "number" && {number}),
        ...(typeof content === "string" && {content}),
    };
};

export const isPasteArtifact = (artifact: Artifact) =>
    artifact.type === ATTACHMENT_ARTIFACT_TYPE &&
    artifact.payload.attachmentType === PASTE_ATTACHMENT_KIND;

/**
 * The number for the next paste on this thread. It is the highest stored
 * number plus one. The backend uses the same rule. The backend counts from
 * the artifacts that stay on the thread. An edited turn reuses the number
 * that it released.
 */
export const nextPasteNumber = (artifacts?: Artifact[]) =>
    (artifacts ?? [])
        .filter(isPasteArtifact)
        .reduce(
            (highest, {payload}) =>
                typeof payload.number === "number" && payload.number > highest
                    ? payload.number
                    : highest,
            0
        ) + 1;

export const readPasteChips = (artifacts?: Artifact[]): PasteChipData[] =>
    (artifacts ?? []).filter(isPasteArtifact).map(readPasteChip);

/** The paste content that a message stores. The application uses it to resend an edited turn. */
export const readPasteAttachments = (
    artifacts?: Artifact[]
): PasteAttachment[] =>
    (artifacts ?? [])
        .filter(isPasteArtifact)
        .filter((artifact) => typeof artifact.payload.content === "string")
        .map(({payload}) => ({
            type: PASTE_ATTACHMENT_KIND,
            content: payload.content as string,
            ...(payload.truncated === true && {truncated: true}),
        }));
